import { __read } from 'tslib';
import { useState } from 'react';
import { motionValue } from '../value/index.js';
import { useOnChange } from '../value/use-on-change.js'; // Does this device prefer reduced motion? Returns `null` server-side.

var prefersReducedMotion;

function initPrefersReducedMotion() {
  prefersReducedMotion = motionValue(null);
  if (typeof window === "undefined") return;

  if (window.matchMedia) {
    var motionMediaQuery_1 = window.matchMedia("(prefers-reduced-motion)");

    var setReducedMotionPreferences = function setReducedMotionPreferences() {
      return prefersReducedMotion.set(motionMediaQuery_1.matches);
    };

    motionMediaQuery_1.addListener(setReducedMotionPreferences);
    setReducedMotionPreferences();
  } else {
    prefersReducedMotion.set(false);
  }
}
/**
 * A hook that returns `true` if we should be using reduced motion based on the current device's Reduced Motion setting.
 *
 * This can be used to implement changes to your UI based on Reduced Motion. For instance, replacing motion-sickness inducing
 * `x`/`y` animations with `opacity`, disabling the autoplay of background videos, or turning off parallax motion.
 *
 * It will actively respond to changes and re-render your components with the latest setting.
 *
 * ```jsx
 * export function Sidebar({ isOpen }) {
 *   const shouldReduceMotion = useReducedMotion()
 *   const closedX = shouldReduceMotion ? 0 : "-100%"
 *
 *   return (
 *     <motion.div animate={{
 *       opacity: isOpen ? 1 : 0,
 *       x: isOpen ? 0 : closedX
 *     }} />
 *   )
 * }
 * ```
 *
 * @return boolean
 *
 * @public
 */


function useReducedMotion() {
  /**
   * Lazy initialisation of prefersReducedMotion
   */
  !prefersReducedMotion && initPrefersReducedMotion();

  var _a = __read(useState(prefersReducedMotion.get()), 2),
      shouldReduceMotion = _a[0],
      setShouldReduceMotion = _a[1];

  useOnChange(prefersReducedMotion, setShouldReduceMotion);
  return shouldReduceMotion;
}

export { useReducedMotion };